import { getCookie } from 'cookies-next';
import { IncomingMessage } from 'http';
import { NextRequest } from 'next/server';

import { regions } from '@/config/locales';
import { countryToRegionMap, regionToMainCountryMap } from '@/config/mock-data/countriesConfig';
import { allLangRegions } from '@/config/mock-data/languageRegions';
import { extractNextDataPagePathname } from '@/middleware/helpers/extractNextDataPagePathname';
import { supportedRegions } from '@/middleware/regional-redirects/configuration';
import {
    CLIENT_REGION_HEADER,
    DEV_LOCATION_COOKIE_KEY,
    EMPTY_STR,
    REGION_IN_PATH,
} from '@/utils/constants/common';
import { getHeaderValue } from '@/utils/server/request-extractor';

import { isLanguageAgnosticRequest, isSearchEngineBot } from '../httpUtil';

import { getLOB, LOB } from './lob-utils';

type RegionParamsType = {
    _clientRegion: string;
};

const DEFAULT_REGION = 'US';

export const REGION_ALIAS_MAPPING = {
    // GB subregions
    GG: 'GB',
    IM: 'GB',
    JE: 'GB',
    // IT subregions
    SM: 'IT',
    VA: 'IT',
    // FR subregions
    BL: 'FR',
    GF: 'FR',
    GLP: 'FR',
    GP: 'FR',
    GY: 'FR',
    MF: 'FR',
    MQ: 'FR',
    PM: 'FR',
    RE: 'FR',
    YT: 'FR',
} as const;

export type LobRegionAliasMappingType = {
    [key in LOB]: {
        [key: string]: string;
    };
};

const LOB_REGION_ALIAS_MAPPING: LobRegionAliasMappingType = {
    pplus: {
        ...REGION_ALIAS_MAPPING,
    },
    ptv: {},
};

export type LobRegionMapForBotsType = {
    [key in LOB]: {
        [key: string]: string;
    };
};

const LOB_REGION_MAP_FOR_BOTS: LobRegionMapForBotsType = {
    pplus: {},
    ptv: {
        ...regionToMainCountryMap,
    },
};

/**
 * URL path segments that denote a PTV region: the country codes in `regions.ptv` plus the
 * marketing regions that appear directly in the path (`latam`, `gsa`, `gb_ie`). Exposed here —
 * alongside the production region-resolution logic that already owns `regionToMainCountryMap` —
 * so callers (e.g. hreflang/noindex path detection) don't reach into the mock-data module
 * directly and stay in sync with the region set the resolver uses.
 */
export const PTV_REGION_URL_SEGMENTS: ReadonlySet<string> = new Set<string>([
    ...regions.ptv,
    ...Object.keys(regionToMainCountryMap ?? {}),
]);

const fallbackDomain = 'https://www.terra-nova.com';

const toPathname = (urlOrPath: string): string | undefined => {
    try {
        return new URL(urlOrPath, fallbackDomain).pathname;
    } catch {}
    return urlOrPath;
};

/**
 * Client-side navigations run getServerSideProps with `req.url = /_next/data/{buildId}/{page}.json`.
 * Bots have no geo fallback, so their region is read from the wrapped page path instead of dropping
 * to US; real users keep ignoring `_next` URLs and resolve by geo header. Null means "ignore".
 */
const getRegionLookupUrl = (request: IncomingMessage | NextRequest): string | null => {
    const url = request.url ?? '';
    if (!url.includes('_next')) {
        return url;
    }

    if (!isLanguageAgnosticRequest(request)) {
        return null;
    }

    return extractNextDataPagePathname(toPathname(url) ?? '');
};

const getUpdatedSegmentsArray = ({
    lob,
    urlArray,
    request,
}: {
    lob: LOB;
    urlArray: string[];
    request: IncomingMessage | NextRequest;
}): string[] => {
    const segmentsArray = urlArray
        .map((url) => {
            const pathname = toPathname(url);
            const regionSegment = pathname?.split('/')?.filter(Boolean)?.[0];
            return regionSegment ? regionSegment.toLowerCase() : undefined;
        })
        .filter((segment) => !!segment) as string[];

    // For language-agnostic requests (crawlers / blank Accept-Language), map marketing
    // segments to their main country (e.g. /latam/ → MX). Because this makes the segment
    // match `regions[lob]` in `getRegionFromUrl` — which runs before the geo-header branch
    // in `getRegion` — the URL region takes precedence over the geo header for these
    // requests (e.g. /latam/ from a US IP → MX). A real user (who sends Accept-Language)
    // keeps the geo header winning (e.g. /latam/ + geo CL → CL).
    return isLanguageAgnosticRequest(request)
        ? segmentsArray.map((segment) => LOB_REGION_MAP_FOR_BOTS?.[lob]?.[segment] || segment)
        : segmentsArray;
};

const getRegionFromUrl = (request: IncomingMessage | NextRequest): string | null => {
    const lookupUrl = getRegionLookupUrl(request);
    if (lookupUrl === null) {
        return null;
    }

    // the ensure U.S region force to null
    // Do not load region from Referer here — a stale Referer
    const requestSegments = (toPathname(lookupUrl) ?? '').split('/').filter(Boolean);
    if (!requestSegments.length) {
        return null;
    }

    // get referer header if available if valid url get the pathname
    // https://www.terra-nova.com is just a throwaway value to ensure URL is valid
    const referer = getHeaderValue(request, 'referer', fallbackDomain);

    // Array of URls to check for /{region}/ pattern
    // Prioritize the current request URL instead of the Referer so the region aligns with the page being served.
    // Otherwise, after a redirect or a DevTools region change, the Referer may still reflect the previous locale
    // (e.g., /de/) while the URL is /mx/, causing the incorrect region to be selected when both are valid.
    const urlArray = [lookupUrl, referer].filter((path) => !!path && path !== '/');
    const lob: LOB = getLOB(request);
    // get array of 1st segments to check for region
    // for bots, map a region segment to proper region if mapping exists
    const actualArray = getUpdatedSegmentsArray({ lob, urlArray, request });

    // find the first segment that matches the region in config for given LOB
    const regionFromUrl = actualArray?.find((segment: string) => regions[lob]?.includes(segment));

    return regionFromUrl?.toLowerCase() ?? null;
};

/**
 * Resolve a PTV marketing-region URL segment (e.g. `latam`, `gsa`, `gb_ie`) to its
 * main country (`mx`, `de`, `gb`). These segments are not country codes, so
 * `getRegionFromUrl` can't match them against `regions[lob]` and resolution would
 * otherwise fall back to `US` for `/latam/...` pages. Used only as a fallback in
 * `getRegion` when no geo header is present, so SSR (e.g. the live-tv channels
 * prefetch, which is scoped by `_clientRegion`) resolves to the correct market
 * instead of US. The geo header still takes precedence, preserving the exact
 * country (e.g. `CL`).
 *
 * Scoped to PTV: marketing regions (`latam`, `gsa`, `gb_ie`) are a PTV concept, so
 * for any other LOB (e.g. P+) we return null and preserve the previous behaviour
 * (fall through to `US`), keeping this change isolated from P+ region resolution.
 */
const getRegionMainCountryFromUrl = (request: IncomingMessage | NextRequest): string | null => {
    const lookupUrl = getRegionLookupUrl(request);
    if (lookupUrl === null) {
        return null;
    }
    const lob: LOB = getLOB(request);
    if (lob !== 'ptv') {
        return null;
    }
    const referer = getHeaderValue(request, 'referer', fallbackDomain);
    const urlArray = [lookupUrl, referer].filter((path) => !!path && path !== '/');
    const segments = getUpdatedSegmentsArray({ lob, urlArray, request });
    // A marketing region maps to a *different* main country (e.g. latam→mx);
    // single-country regions (us→us) and bare country codes are already handled by
    // getRegionFromUrl, so they're intentionally skipped here.
    const marketingSegment = segments.find(
        (segment) =>
            !!regionToMainCountryMap[segment] && regionToMainCountryMap[segment] !== segment,
    );
    return marketingSegment ? regionToMainCountryMap[marketingSegment] : null;
};

/**
 * The geo header arrives unvalidated. Accept only regions the platform can serve
 * (plus subregion aliases, resolved to their target right after) so an unsupported geo falls
 * through to the default-region chain instead of resolving to a region with no config,
 * translations, or page attributes. P+ only — PTV marketing-region semantics stay untouched.
 */
const isServableGeoRegion = (lob: LOB, region: string): boolean =>
    lob !== 'pplus' ||
    supportedRegions.includes(region.toLowerCase()) ||
    region.toUpperCase() in LOB_REGION_ALIAS_MAPPING.pplus;

const getRegion = (request?: IncomingMessage): string => {
    // If no request object return default region
    if (!request) {
        return DEFAULT_REGION;
    }

    // For bots, first check header
    // To handle cases where region is not in the URL but was detected earlier
    const override = getHeaderValue(request, REGION_IN_PATH);
    if (override !== EMPTY_STR) {
        return override.toUpperCase();
    }

    // Check URL and Referer for region. If found return it.
    // This is to handle cases where the region is in the URL path.
    // We want to prioritize this over cookies and headers with the expectation that middleware would handle regional redirects.
    const regionFromUrl = getRegionFromUrl(request);
    if (regionFromUrl) {
        return regionFromUrl.toUpperCase();
    }

    // If request is a search engine bot and no region is found in the URL or referer, return default region
    if (isSearchEngineBot(request)) {
        return DEFAULT_REGION;
    }

    const isDebug = process.env.APP_DEBUG === 'true';
    const devRegion = isDebug && <string>getCookie(DEV_LOCATION_COOKIE_KEY, { req: request });
    const clientRegionHeader = getHeaderValue(request, CLIENT_REGION_HEADER);
    const lob = getLOB(request);

    // Precedence: dev override cookie → client-region (geo) header → marketing-region
    // URL fallback → US. The geo header wins when present so the exact country is kept
    // (e.g. CL) — but for P+ only when the platform can actually serve that region;
    // unsupported geos (e.g. PL) fall through instead of leaking into
    // config/translation fetches. Only when there's no dev cookie and no (servable) geo
    // header do we fall back to a marketing-region URL's main country (e.g. /latam/ →
    // MX) instead of US, so `/latam/`, `/gsa/`, `/gb_ie/` pages aren't served US content.
    const regionKey =
        (devRegion || undefined) ??
        (clientRegionHeader !== EMPTY_STR && isServableGeoRegion(lob, clientRegionHeader)
            ? clientRegionHeader
            : undefined) ??
        getRegionMainCountryFromUrl(request) ??
        DEFAULT_REGION;

    const region = regionKey.toUpperCase();
    // check for subregion alias
    if (lob in LOB_REGION_ALIAS_MAPPING && region in LOB_REGION_ALIAS_MAPPING[lob]) {
        return LOB_REGION_ALIAS_MAPPING[lob][region];
    }

    return <string>region;
};

const getRegionFromNextRequest = (request: NextRequest): string => {
    let region = request.headers.get(CLIENT_REGION_HEADER) ?? 'US';

    if (process.env.APP_DEBUG === 'true') {
        const regionFromCookie = request.cookies.get(DEV_LOCATION_COOKIE_KEY);

        if (regionFromCookie) {
            region = regionFromCookie.value;
        }
    }

    const upperCasedRegion = region.toUpperCase();
    const lob = getLOB(request);

    // Resolve subregion aliases here rather than at each consumer. The regional-redirect
    // middleware checks the region against `supportedRegions` before anything else runs, so a
    // raw `GG` reads as unservable and the visitor is bounced to the intl landing even though
    // `GB` is supported. Legacy resolves the alias at the same point, in the Region
    // constructor, which is why it serves these visitors correctly today. PTV's alias map is
    // empty, so this is a no-op there.
    return LOB_REGION_ALIAS_MAPPING[lob]?.[upperCasedRegion] ?? upperCasedRegion;
};

const getRegionParams = (region: string): RegionParamsType => {
    return {
        _clientRegion: region,
    };
};

const isValidRegion = (region: string) => allLangRegions.some((lr) => lr.region === region);

const isValidLanguage = (langString: string | null) =>
    allLangRegions.some((lr) => lr.lang === langString);

const getRegionObject = (region: string) => allLangRegions.find((lr) => lr.region === region);

const getLegacyRegion = (region: string): string => getRegionObject(region)?.legacyRegion ?? region;

// convert country code to marketing region
const getMarketingRegion = (countryCode: string | null): string | undefined => {
    if (countryCode) {
        return countryToRegionMap[countryCode.toLowerCase()];
    }
};

// getMarketingRegion with feature flag
const getMarketingRegionIfEnabled = (allowMarketingRegion = false, region: string): string => {
    return allowMarketingRegion ? (getMarketingRegion(region) ?? region) : region;
};

export {
    getLegacyRegion,
    getMarketingRegion,
    getMarketingRegionIfEnabled,
    getRegion,
    getRegionFromNextRequest,
    getRegionFromUrl,
    getRegionObject,
    getRegionParams,
    getUpdatedSegmentsArray,
    isValidLanguage,
    isValidRegion,
};
